import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

interface MePermissionsResponse {
  readonly permissions: readonly string[];
}

/**
 * Permissões efetivas (`module:action`) do usuário logado. Carregadas do backend
 * (`GET /me/permissions`) — o front NÃO decodifica permissões do token. É a
 * fonte de verdade da UI para mostrar/ocultar features por permissão granular,
 * espelhando o que o authorizer resolve no servidor.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly _permissions = signal<readonly string[]>([]);
  private loaded = false;

  readonly permissions = this._permissions.asReadonly();

  /** Carrega as permissões efetivas uma única vez (idempotente). */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) {
      return;
    }
    await this.reload();
  }

  /** Recarrega do backend (ex.: após a concessão/revogação de grants). */
  async reload(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<MePermissionsResponse>(`${environment.apiUrl}/me/permissions`),
      );
      this._permissions.set(response?.permissions ?? []);
      this.loaded = true;
    } catch {
      this._permissions.set([]);
    }
  }

  /** Esquece as permissões (no logout). */
  clear(): void {
    this._permissions.set([]);
    this.loaded = false;
  }

  /** Indica se o usuário possui a permissão `code`. */
  has(code: string): boolean {
    return this._permissions().includes(code);
  }

  /** True se nenhuma permissão é exigida ou se o usuário tem ao menos uma. */
  hasAny(codes?: readonly string[]): boolean {
    if (!codes || codes.length === 0) {
      return true;
    }
    return codes.some((code) => this.has(code));
  }

  /**
   * Predicado que gateia ações da UI (botões, seções): o usuário possui a
   * permissão `code`. Pós-flip a permissão é a única fonte — não há mais bypass
   * por grupo ADMIN (espelha o authorizer/backend).
   */
  can(code: string): boolean {
    return this.has(code);
  }

  /**
   * Versão para um conjunto: sem exigência → liberado; senão exige ao menos uma
   * das permissões. Usado por guards e pelo filtro do menu.
   */
  canAny(codes?: readonly string[]): boolean {
    return this.hasAny(codes);
  }

  /**
   * Exige TODAS as permissões: sem exigência → liberado; senão o usuário precisa
   * possuir cada uma delas. Usado quando um recurso combina mais de um módulo
   * (ex.: a documentação de gestão de acessos exige `usuarios` E `permissoes`).
   */
  canAll(codes?: readonly string[]): boolean {
    if (!codes || codes.length === 0) {
      return true;
    }
    return codes.every((code) => this.has(code));
  }
}
